import type { ButtonHTMLAttributes } from 'react';
import { styled, css } from 'styled-components';

interface StyledItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  $checked: boolean;
  disabled?: boolean;
  $allDisabled?: boolean;
  $columns: number;
  $index: number;
  $totalCount: number;
}

const RADIUS = '8px';

const cornerRadii = ($index: number, $totalCount: number, $columns: number) => {
  const col = $index % $columns;
  const hasTop = $index >= $columns;
  const hasBottom = $index + $columns < $totalCount;
  const hasLeft = col > 0;
  const hasRight = col + 1 < $columns && $index + 1 < $totalCount;

  const isTopLeft = !hasTop && !hasLeft;
  const isTopRight = !hasTop && !hasRight;
  const isBottomLeft = !hasBottom && !hasLeft;
  const isBottomRight = !hasBottom && !hasRight;

  return css`
    border-top-left-radius: ${isTopLeft ? RADIUS : '0'};
    border-top-right-radius: ${isTopRight ? RADIUS : '0'};
    border-bottom-left-radius: ${isBottomLeft ? RADIUS : '0'};
    border-bottom-right-radius: ${isBottomRight ? RADIUS : '0'};
  `;
};

export const StyledItem = styled.button.attrs(() => ({
  type: 'button',
}))<StyledItemProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 8px 16px;
  height: 36px;
  box-sizing: border-box;
  border: 1px solid ${({ theme, $allDisabled }) => ($allDisabled ? theme.colors.grey05_3 : theme.colors.grey30_1)};
  margin-left: ${({ $index, $columns }) => ($index % $columns === 0 ? '0' : '-1px')};
  margin-top: ${({ $index, $columns }) => ($index < $columns ? '0' : '-1px')};

  ${({ $index, $totalCount, $columns }) => cornerRadii($index, $totalCount, $columns)}

  background: ${({ $checked, disabled, theme }) => {
    if ($checked && disabled) return theme.colors.grey70_1;
    if (!$checked && disabled) return theme.colors.grey05_3;
    return theme.colors.grey00_1;
  }};
  color: ${({ $checked, disabled, theme }) => {
    if ($checked && disabled) return theme.colors.grey30_1;
    if (!$checked && disabled) return theme.colors.grey70_3;
    if ($checked && !disabled) return theme.colors.black_1;
    return theme.colors.grey100_1;
  }};

  &:after {
    content: '';
    height: 2px;
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    background: ${({ $checked, disabled, theme }) => {
      if ($checked && disabled) return theme.colors.grey70_1;
      if ($checked && !disabled) return theme.colors.main;
      return '';
    }};
  }

  &:hover {
    ${({ $checked, disabled }) =>
      $checked
        ? css`
            cursor: ${disabled ? 'not-allowed' : 'default'};
          `
        : css`
            cursor: ${disabled ? 'not-allowed' : 'pointer'};
          `}
  }

  &:focus-visible {
    outline: 0;
    z-index: 1;
    ${({ theme }) => theme.shadows.focus}
  }

  ${({ theme }) => theme.typography.textMedium};
  ${({ theme }) => theme.typography.weightMedium}
`;
